import * as arr from "./helpers/array";
import {getSheetData} from "./operations/data";
import {DataStore} from "@xbs/muon";
import {loadCell} from "./operations/types";
import {removeUnusedStyles, styleFromText, styleToText} from "./operations/styles";
import {getFormatFromData, checkFormat} from "./operations/formats";
import {updateMaxColumn, encode, escapeSheet} from "./helpers/column_names";

const options = [
	"rename-sheet",
	"remove-sheet",
	"copy-sheet",
	"hide-sheet"
];

const actionMap = {
	"remove-sheet": view => _remove(view),
	"rename-sheet": view => edit(view),
	"new-sheet": view => add(view),
	"copy-sheet": view => copy(view),
	"hide-sheet": view => setState(view, view._activeSheet, "hidden")
};

export function init(view){
	view.attachEvent("onComponentInit", () => ready(view));
}

export const numbers = [0.5, 0.75, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 3, 4];

export function defaultBottom(view){
	const locale = webix.i18n.spreadsheet.tooltips;
	const readonly = view.config.readonly;
	const tabWidth = view.config.sheetTabWidth;
	const minTabWidth = tabWidth == "auto" ? view.defaults.sheetTabWidth : tabWidth;

	const toolbar =  {
		view: "toolbar",
		id: "bottom-toolbar",
		css: "webix_ssheet_bottom_toolbar webix_layout_toolbar",
		paddingY: 0,
		height: 34,
		elements:[
			{
				hidden:readonly,
				padding: {
					top: 2,
					bottom: 2,
					left: 4
				},
				cols:[
					{
						view:"icon", icon:"wxi-plus", width:30, id: "add-sheet", click: ()=> add(view),
						tooltip: webix.i18n.spreadsheet.tooltips["add-sheet"]
					},
					{
						view:"icon", icon:"wxi-dots", width:30, id: "show-sheet", popup: getSheetsPopup(view),
						tooltip: webix.i18n.spreadsheet.tooltips["show-sheet"]
					}
				]
			},
			{
				id:"sheets",
				view:"sheets",
				maxWidth: 5 * minTabWidth,
				minWidth: minTabWidth,
				editable:!readonly,
				editaction: "dblclick",
				editor:"text",
				editValue:"value",
				type:{
					width: tabWidth,
					height: 32,
					template: function(obj){
						return "<div>"+obj.value+"</div>"+
							(!readonly?"<div class='webix_input_icon wxi-menu-down webix_ssheet_sheet_arrow'></div>":"");
					},
					//remove skin selection css on dnd
					css:"ssheet_order_sheets"
				},
				onClick:{
					webix_ssheet_sheet_arrow: function(e, id){
						initSheetMenu(view, e, id);
					}
				},
				on:{
					onItemClick: function(id){
						if(id != view._activeSheet)
							view.showSheet(id);
					}
				}
			},
			{
				view: "button", type:"htmlbutton", width: 34, id: "prev-sheet", disabled:true, hidden:true,
				label: "<span class='webix_icon wxi-menu-left'></span>", batch:"pager",
				click:function(){scrollSheets(view, -1);}
			},
			{
				view: "button", type:"htmlbutton", width: 34, id: "next-sheet", hidden:true,
				label: "<span class='webix_icon wxi-menu-right'></span>", batch:"pager",
				click:function(){scrollSheets(view, 1);}
			},
			{gravity:0.000001},
			{
				view: "ssheet-zoom",
				numbers,
				value: 1,
				width: 108,
				tooltip: locale["scale"],
				id: "zoom",
				on: {
					onChange: (v, oldV, config) => {
						if (config == "user") view.zoom(v);
					}
				},
			}
		]
	};

	view.callEvent("onViewInit", ["bottom-toolbar", toolbar]);

	return toolbar;
}

function scrollSheets(view, direction){
	const sheets = view.$$("sheets");
	let step = view.config.sheetTabWidth;
	if(step == "auto")
		step = view.defaults.sheetTabWidth;
	sheets.scrollTo(sheets.getScrollState().x+(direction*step), 0);
}

function menuInit(view){
	var data = [];
	for(var i=0; i < options.length;i++)
		data.push({id: options[i], value:webix.i18n.spreadsheet.menus[options[i]]||options[i]});

	var menu =  {
		view: "ssheet-suggest",
		data: data
	};
	view.callEvent("onViewInit", ["sheet-menu", menu]);
	return menu;
}

export function reset(view, obj){
	const tabs = view.$$("sheets");
	if (tabs){
		tabs.clearAll();
		const set = [];
		obj.sheets.forEach(function(sheet){
			set.push({id:sheet.name, value: sheet.name});
		});
		tabs.parse(set);
		_filterTabs(view);
	}
}

function ready(view){
	prepare(view);

	view.attachEvent("onAfterSheetShow", (name) => selectSheet(view,name));

	view.attachEvent("onCommand", function(command){
		if(actionMap[command.id])
			actionMap[command.id](this);
	});

	if(view.$$("sheets")){
		view.$$("sheets").data.attachEvent("onStoreUpdated", () => {
			changePagerVisability(view);
			changePagerStatus(view);
		});
		view.$$("sheets").attachEvent("onAfterRender", webix.once(() => {
			webix.delay(()=> changePagerVisability(view));
		}));
		view.$$("sheets").attachEvent("onAfterScroll", () => changePagerStatus(view));
		view.$$("sheets").attachEvent("onAfterDrop", (context) => dataOrder(view,context.start,context.index));
		view.$$("sheets").attachEvent("onAfterEditStop", (state) => rename(view,state.old,state.value));
	}
}

function getSheetsPopup(view){
	return {
		view: "popup",
		css: "webix_ssheet_suggest",
		width: 125,
		body:{
			view: "list",
			select: true,
			css: "webix_ssheet_show_list",
			template: "<span class='webix_icon wxi-#icon#'></span>#id#",
			on:{
				onAfterSelect: function(id) {
					setState(view, id, "visible");
					view.showSheet(id);
					this.getParentView().hide();
				}
			},
			maxHeight: 200,
			autoheight: true
		},
		on:{
			onShow: function(){
				this.getBody().showItem(view.getActiveSheet());
			},
			onBeforeShow: function(){
				const data = view._sheets
					.filter(sheet => sheet.state != "veryHidden")
					.map(sheet => {
						return {
							id: sheet.name,
							icon: sheet.state == "hidden" ? "eye-slash" : "eye"
						};
					});

				const list = this.getBody();
				list.clearAll();
				list.parse(data);
				list.select(view.getActiveSheet());
			}
		}
	};
}

function initSheetMenu(view, e, id){
	if(!view.$sheetMenu){
		view.$sheetMenu = webix.ui(menuInit(view));
		view._destroy_with_me.push(view.$sheetMenu);
		view.$sheetMenu.attachEvent("onItemClick", id => callAction(view,id));
	}

	const oneSheet = view.$$("sheets").count() == 1;

	if(view.$sheetMenu.getItem("remove-sheet"))
		view.$sheetMenu[oneSheet ? "disableItem" : "enableItem"]("remove-sheet");

	if(view.$sheetMenu.getItem("hide-sheet"))
		view.$sheetMenu[oneSheet ? "disableItem" : "enableItem"]("hide-sheet");

	if(view.callEvent("onBeforeSheetMenu", [id]))
		view.$sheetMenu.show(e);
}

function changePagerStatus(view){
	const sheets = view.$$("sheets");
	const scrollPos = sheets.getScrollState().x;
	if(scrollPos == 0)
		view.$$("prev-sheet").disable();
	else{
		view.$$("prev-sheet").enable();
		if(sheets.$view.scrollWidth - scrollPos == sheets.$width)
			return view.$$("next-sheet").disable();
	}
	view.$$("next-sheet").enable();
}

function changePagerVisability(view){
	const sheets = view.$$("sheets");
	view.$$("bottom-toolbar").showBatch("pager", sheets.$width != sheets.$view.scrollWidth);
}

function dataOrder(view, name, num){
	for (let id in view._sheets) {
		if (view._sheets[id].name == name) {
			const item = view._sheets.splice(id, 1);
			view._sheets.splice(num, 0, item[0]);
			break;
		}
	}
}

export function setState(view, sheet, state){
	const tab = getSheet(view, sheet);

	if(state == tab.state || (state == "visible" && !tab.state))
		return;

	if(state != "visible"){
		const visibleSheets = getVisible(view);

		//don't hide last visible tab
		if(visibleSheets.length == 1 && visibleSheets[0] === tab)
			return;

		//select the previous sheet in case we hide the selected sheet
		if(sheet == view._activeSheet)
			showPrevious(view, sheet);
	}

	tab.state = state;
	_filterTabs(view);

	view.callEvent("onSheetStateChange", [sheet, state]);
}

function _filterTabs(view){
	const tabs = view.$$("sheets");
	if(tabs)
		tabs.filter(obj => {
			const state = getSheet(view, obj.id).state;
			return state != "hidden" && state != "veryHidden";
		});
}

export function add(view, content){
	var index, name, newIndex;

	index = arr.findIndex(view._sheets, x => x.name == view._activeSheet);
	newIndex = index>-1?index+1:view._sheets.length;
	name = getNewTitle(view);
	content = content || {data:[]};
	view._sheets.splice(newIndex, 0, {
		name: name,
		content: content
	});

	if(view.$$("sheets"))
		view.$$("sheets").add({id: name, value: name},newIndex);

	view._mData.addPage(name);

	parseToStore(view, name, content, view.config.prepareData);

	view.showSheet(name);
	view.callEvent("onSheetAdd", [name]);
	return name;
}

function getNewTitle(view){
	var index = view._sheets.length+1;
	while(getSheet(view, (webix.i18n.spreadsheet.labels.sheet+index)))
		index++;
	var name = webix.i18n.spreadsheet.labels.sheet+index;
	return name;
}

function selectSheet(view,name){
	if(view.$$("sheets")){
		view.$$("sheets").select(name);
		view.$$("sheets").showItem(name);
	}
}

export function edit(view,name){
	if(!name)
		name = view._activeSheet;
	view.$$("sheets").edit(name);
}

function _remove(view){
	view.confirm({
		text: webix.i18n.spreadsheet.labels["sheet-remove-confirm"]
	}).then(()=>{
		remove(view, view._activeSheet);
	});
}

function getVisible(view){
	return view._sheets.filter(s => s.state != "hidden" && s.state != "veryHidden");
}

function showPrevious(view, name){
	const visibleSheets = getVisible(view);

	let index = arr.findIndex(visibleSheets, x => x.name == name);
			
	let prevIndex = index - 1;
	if(!visibleSheets[prevIndex])
		prevIndex = 0;
	visibleSheets.splice(index, 1);

	view.showSheet(visibleSheets[prevIndex].name);
}

export function remove(view, name){
	if(view._sheets.length > 1){
		const visibleSheets = getVisible(view);

		//don't remove last visible tab
		if(visibleSheets.length == 1 && visibleSheets[0] === getSheet(view, name))
			return;

		//select the previous sheet in case we remove the selected sheet
		if(view._activeSheet == name)
			showPrevious(view, name);

		const index = arr.findIndex(view._sheets, x => x.name == name);
		view._sheets.splice(index, 1);
		view._mData.removePage(name);

		if(view.$$("sheets")){
			view.$$("sheets").remove(name);
			view.$$("sheets").refresh();
		}

		view.callEvent("onSheetRemove", [name]);
	}
}

export function rename(view,name,newName){
	if (name == newName) return;

	//correct sheet name
	var i = 1;
	while (!newName || getSheet(view, newName)){
		newName = webix.i18n.spreadsheet.labels.sheet + i;
		i++;
	}
	newName = newName.replace(/[*?:[\]\\/]/g,"").substring(0, 31);

	var sheet = getSheet(view, name);
	var sheets = view.$$("sheets");

	sheet.name = newName;
	if(view._activeSheet == name)
		view._activeSheet = newName;

	if(sheets){
		let item = sheets.getItem(name);
		item.value = newName;
		sheets.data.changeId(name,newName);
		sheets.refresh(newName);
	}

	view._mData.renamePage(name, newName);
	view.callEvent("onSheetRename", [name, newName]);
}

export function serialize(view, obj, config){
	const active = getSheet(view, view._activeSheet);
	active.content = obj;
	active.state = active.state || "visible";

	const sheets = webix.copy(view._sheets);

	const math = !config || config.math!==false;
	const compactStyles = config && config.compactStyles;

	sheets.forEach(sheet => {
		if(sheet.name != view._activeSheet){
			sheet.content.data = getSheetData(view, math, sheet.name);
			sheet.state = sheet.state || "visible";
			if(compactStyles)
				sheet.content.styles = removeUnusedStyles(sheet.content);
			sheet.content.ranges = view.ranges.serialize(sheet.name);
		}
	});

	return sheets;
}

function parseToStore(view, name, obj, prepareData){
	view.ranges.parse(obj.ranges, name);

	webix.extend(obj, {
		styles: [],
		formats: [],
		editors: []
	});

	const page = view._mData.getPage(name);
	const data = obj.data.map(arr => {
		const invalidValue = webix.isUndefined(arr[2]) || arr[2] === null;

		if(prepareData){
			if(invalidValue)
				arr[2] = "";
			else {
				const linkRegex = /^=link\(/i;
				if(typeof arr[2] == "string" && linkRegex.test(arr[2])){
					// LINK is deprecated
					arr[2] = arr[2].replace(linkRegex, "=HYPERLINK(");
				}
			}

			const cell = loadCell(view, obj, arr);
			//cell value and type can be changed via types module
			arr[2] = cell.value;
			arr[3] = arr[3] || "";
			arr[4] = cell.type;

			prepareCell(arr, obj);
		}
		else if(invalidValue)
			throw `unsupported value in ${escapeSheet(name)}!${encode[arr[0]]}${arr[1]}`;

		return [ arr[0]-1, arr[1]-1, {
			value: arr[2],
			type: arr[4],
			style: arr[3],
			$format: checkFormat({ fmt: getFormatFromData(obj, arr) }, "string") ? 1 : 0
		}];
	});

	page.parse(data, true);
}

export function prepareCell(cell, data, update){
	const isDate = cell[4] == "date";
	if(isDate || update){
		const isString = typeof cell[2] == "string";

		const link = update && isString && /^=hyperlink\(/i.test(cell[2]);

		const styleProperties = cell[3] ? arr.find(data.styles, style => style[0] == cell[3]) : null;
		const styleObj = styleProperties ? styleFromText(styleProperties[1]) : {};

		if((isDate && !styleProperties || !styleObj.format) || link){
			let props = {};

			if(link){
				cell[3] = "wss_link" + (styleProperties ? "_"+cell[3] : "");
				props = { color: "#0000ff", underline: "underline" };
			}
			else if(isString && /^=time\(/i.test(cell[2])){
				cell[3] = "wss_load_time" + (styleProperties ? "_"+cell[3] : "");
				props.format = cell[3]+"_fmt";
				data.formats.push([props.format, webix.i18n.spreadsheet.formats.timeFormat]);
			}
			else{
				cell[3] = "wss_load_date" + (styleProperties ? "_"+cell[3] : "");
				props.format = "date";
			}

			if(!arr.find(data.styles, style => style[0] == cell[3]))
				data.styles.push([
					cell[3],
					styleToText({props: webix.extend(styleObj, props, true)})
				]);
		}
	}
}

export function load(view, obj, excel){
	view._loading_data = true;
	obj = webix.isArray(obj)?{sheets:obj}:obj;
	const prepareData = view.config.prepareData || excel;

	if(!obj.sheets && view._activeSheet){
		//loading into active sheet
		const name = view._activeSheet;
		getSheet(view, name).content = obj;
		parseToStore(view, name, obj, prepareData);
		refresh(view, obj);
	} else {
		//creating new set of sheets
		prepare(view, obj, prepareData);
	}
	view._loading_data = false;
}

export function prepare(view, obj, prepareData){
	if(!obj) obj = {data:[]}; 
	if(!obj.sheets){
		obj = newSheet(obj);
	}

	view._activeSheet = "";
	view._sheets = obj.sheets;
	view._mData = new DataStore({strict: view.config.strict});

	// first add all pages then parse data in them
	obj.sheets.forEach(sheet => {
		view._mData.addPage(sheet.name);
	});

	obj.sheets.forEach(sheet => {
		const content = sheet.content;
		const sheetName = sheet.name;

		parseToStore(view, sheetName, content, prepareData);
	});

	reset(view, obj);
	show(view, getVisible(view)[0].name);
}

export function newSheet(obj){
	return {
		sheets:[{
			name: webix.i18n.spreadsheet.labels.sheet + 1,
			content: obj
		}]
	};
}

export function show(view, name){
	if (name == view._activeSheet) return;

	if(view.callEvent("onBeforeSheetShow", [name])){
		if (view._activeSheet)
			getSheet(view, view._activeSheet).content = view.serialize();

		view._activeSheet = name;
		const obj = getSheet(view, name).content;

		view._mPage = view._mData.getPage(name);

		refresh(view, obj);

		view.callEvent("onAfterSheetShow", [name]);
	}
}

export function getSheet(view, name){
	return arr.find(view._sheets, x => x.name == name);
}

export function isVisible(view, sheet){
	sheet = getSheet(view, sheet);
	return sheet.state != "hidden" && sheet.state != "veryHidden";
}

export function eachSheet(view, callback){
	for (var i=0; i<view._sheets.length; i++)
		callback(view._sheets[i].name);
}

export function getActive(view){
	return view._activeSheet;
}

function refresh(view, obj){
	const data = getSheetData(view);

	let [rows, cols] = arr.getDimension(data, obj.spans, view.config.rowCount, view.config.columnCount);

	if (rows != view.config.rowCount || cols != view.config.columnCount){
		view.config.rowCount = rows;
		view.config.columnCount = cols;
		view._resetTable();
	}

	updateMaxColumn(cols);

	view.callEvent("onReset", []);

	view._loading_data = true;
	view.callEvent("onDataParse", [obj]);
	view._loading_data = false;

	view._table.refresh();
	if(view.config.bottombar)
		view.$$("sheets").refresh();
}

function copy(view){
	add(view, view.serialize());
}

function callAction(view, id){
	if(!view.$sheetMenu.data.getMark(id, "webix_disabled")){
		if(actionMap[id])
			actionMap[id](view);
		else
			view.callEvent("onCommand", [{id}]);
	}
}

